<script setup>
import EditTicketAnswerAction from '@frontJs/components/actions/projectManagement/tasks/EditTicketAnswerAction.vue';
import GenerateAITicketAnswerAction from '@frontJs/components/actions/projectManagement/tasks/GenerateAITicketAnswerAction.vue';
import CircuitryIcon from '@iconify-vue/ph/circuitry';
import EyeIcon from '@iconify-vue/ph/eye';
import PaperPlaneTiltIcon from '@iconify-vue/ph/paper-plane-tilt';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import BubbleLine from '@js/components/chats/BubbleLine.vue';
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import FilePondField from '@js/components/fields/FilePondField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { reactive, ref, toRef, watch } from 'vue';
import { mixed, object, string } from 'yup';

const {
    ticket,
    callback,
    defaultOpen = false,
} = defineProps({
    ticket: {
        type: Object,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
    defaultOpen: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const innerTicket = toRef(() => ticket);

const loading = ref(false);

const chat = ref(null);

const isOpen = ref(defaultOpen);

const hasChanges = ref(false);

const editorKey = ref(0);

const priorities = ref(null);

const statuses = ref(null);

const attachmentsField = ref(null);

const attachmentsUploading = ref(false);

const form = reactive({
    message: '',
    attachments: [],
    priority: null,
    status: null,
});

const validationSchema = object({
    message: string().required('Le message est requis'),
    attachments: mixed().nullable(),
    priority: object().required('La priorité est requise'),
    status: object().required('Le statut est requis'),
});

function setIsOpen(value) {
    isOpen.value = value;
}

watch(isOpen, (value) => {
    if (value) {
        updateData();

        return;
    }

    if (callback && hasChanges.value) {
        hasChanges.value = false;
    }

    if (callback) {
        callback();
    }
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.get(route('front.projectManagement.task.getTicketPriorities')),
        axios.get(route('front.projectManagement.task.getTicketStatuses')),
        axios.get(
            route('front.projectManagement.task.getTicket', {
                ticket: ticket.id,
            }),
        ),
    ])
        .then((response) => {
            priorities.value = response[0].data;
            statuses.value = response[1].data;

            innerTicket.value.orderedAnswers = response[2].data.orderedAnswers;

            form.priority = priorities.value.find(
                (priority) => priority.value === ticket.priority.value,
            );

            form.status = statuses.value.find(
                (status) => status.value === ticket.status.value,
            );
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
            scrollToBottom();
        });
}

function saveAction() {
    loading.value = true;

    axios
        .post(
            route('front.projectManagement.task.postTicketAnswer', {
                ticket: innerTicket.value.id,
            }),
            {
                message: form.message,
                status: form.status.value,
                priority: form.priority.value,
                attachments: form.attachments,
            },
        )
        .then((response) => {
            hasChanges.value = true;
            innerTicket.value.orderedAnswers =
                response.data.ticket.orderedAnswers;
            form.message = '';
            form.attachments = [];
            attachmentsField.value?.reset();
            scrollToBottom();
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

function scrollToBottom() {
    let interval = setInterval(() => {
        if (chat.value) {
            chat.value.parentNode.style.scrollBehavior = 'smooth';
            chat.value.parentNode.scrollTop = chat.value.scrollHeight;
            clearInterval(interval);
        }
    }, 100);
}
</script>

<template>
    <slot name="button" :set-is-open="setIsOpen" />

    <DialogDrawer
        v-model="isOpen"
        size="almost-full"
        :loading="loading"
        resizable-footer
        min-footer-height="420"
        footer-storage-key="centralApikomChatTicketFooterHeight"
    >
        <template #title> Chat - {{ ticket.fullName }} </template>

        <template #content>
            <div ref="chat" class="flex flex-col gap-4">
                <BubbleLine
                    v-for="(orderAnswer, index) in innerTicket.orderedAnswers"
                    :key="index"
                    :name="orderAnswer.realName"
                    :message="orderAnswer.message"
                    :date="formatDate(orderAnswer.createdAt, false, true)"
                    :attachments="orderAnswer.attachments"
                    :right="!orderAnswer.isCustomer"
                    :is-wysiwyg-html="!orderAnswer.isCustomer"
                >
                    <template
                        v-if="!orderAnswer.isCustomer && orderAnswer.readAt"
                        #status
                    >
                        <span
                            class="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset"
                            :title="
                                'Lu par le client le ' +
                                formatDate(orderAnswer.readAt, false, true)
                            "
                        >
                            <EyeIcon class="size-3.5" />
                            Lu le
                            {{ formatDate(orderAnswer.readAt, false, true) }}
                        </span>
                    </template>

                    <template #edit-action>
                        <EditTicketAnswerAction
                            :ticket="ticket"
                            :ticket-answer="
                                orderAnswer.ticketAnswerId
                                    ? {
                                          id: orderAnswer.ticketAnswerId,
                                          message: orderAnswer.message,
                                      }
                                    : undefined
                            "
                            :callback="updateData"
                        >
                            <template #trigger>
                                <div class="absolute inset-0 size-full">
                                    &nbsp;
                                </div>
                            </template>
                        </EditTicketAnswerAction>
                    </template>
                </BubbleLine>
            </div>
        </template>

        <template #footer>
            <FormRoot
                :validation-schema="validationSchema"
                v-slot="{ errors }"
                :submit="saveAction"
            >
                <div class="flex justify-end gap-2">
                    <ListboxField
                        v-model="form.status"
                        field-id="status"
                        field-name="status"
                        label="Statut"
                        :options="statuses"
                        inline-label
                        size="small"
                    >
                        <template #buttonContent>
                            {{ form.status.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <ListboxField
                        v-model="form.priority"
                        field-id="priority"
                        field-name="priority"
                        label="Priorité"
                        :options="priorities"
                        inline-label
                        size="small"
                    >
                        <template #buttonContent>
                            {{ form.priority.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>
                </div>

                <EditorField
                    :key="editorKey"
                    v-model="form.message"
                    field-name="message"
                    field-id="message"
                    size="full"
                    disable-html-toggle
                />

                <div class="flex items-center justify-between gap-2">
                    <div class="flex items-center gap-2">
                        <FilePondField
                            ref="attachmentsField"
                            v-model="form.attachments"
                            field-name="attachments"
                            field-id="attachments"
                            accept="image/png, image/jpeg, image/jpg, application/pdf, text/plain, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv"
                            compact
                            @processing="attachmentsUploading = $event"
                        />

                        <GenerateAITicketAnswerAction
                            :ticket="ticket"
                            :current-draft="form.message"
                            :callback="
                                (answer) => {
                                    form.message = answer;
                                    editorKey++;
                                }
                            "
                        >
                            <template #trigger>
                                <ActionButton
                                    :left-icon="CircuitryIcon"
                                    size="small"
                                    title="Générer une réponse avec l'IA"
                                    type="primary"
                                >
                                    IA
                                </ActionButton>
                            </template>
                        </GenerateAITicketAnswerAction>
                    </div>

                    <ActionButton
                        submit
                        :disabled="
                            Object.keys(errors).length > 0 ||
                            loading ||
                            attachmentsUploading
                        "
                        :loading="loading"
                        size="small"
                        :left-icon="PaperPlaneTiltIcon"
                    >
                        Envoyer
                    </ActionButton>
                </div>
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
